import { useEffect, useState } from "react";
import {
  versionsFieldMessages,
  versionsRequestFields,
  type VersionsValue,
} from "@shared-core/domain/line-versions";
import { VersionsSplit } from "../catalogue/VersionsControl";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

const QUANTITIES = "quantities";
const VERSION_SPLIT = "version_split";
const VERSION_QUANTITIES = "version_quantities";

/** What the dry run says about the line's versions (`set_draft_quote_line_quantity_options._versions_form`). */
interface LineVersionsContext {
  count: number;
  labels: string[];
  quantities: number[];
  step: number;
}

function contextOf(raw: unknown): LineVersionsContext | null {
  const c = raw as Partial<LineVersionsContext> | undefined;
  return c &&
    typeof c.count === "number" &&
    Array.isArray(c.labels) &&
    typeof c.step === "number"
    ? (c as LineVersionsContext)
    : null;
}

/** The quantities the form offers, as typed so far: a row with no whole quantity yet counts as none. */
function quantitiesOffered(body: Record<string, unknown>): (number | null)[] {
  const rows = Array.isArray(body[QUANTITIES]) ? body[QUANTITIES] : [];
  return rows.map((row) => {
    const quantity = (row as { quantity?: unknown } | null)?.quantity;
    return typeof quantity === "number" && quantity > 0 ? quantity : null;
  });
}

/**
 * How a draft quote line of several versions shares its quantity (order to invoice doc 108 §3.2), inside *Change
 * quantities…*. The count is the spec and cannot change here (a different count is a new line), so only the split is
 * drawn. With one quantity, Split evenly re-splits it and Enter quantities asks for each version's. With several
 * quantities the split is given when ordered (spec 53, ruling 40): each quantity is priced for all the versions, the
 * customer gives each version's quantity when they choose one, and an entered split is put back to even (the commit
 * refuses one sent with several). A line of one version has no hint and draws nothing. The dry run's prefill is the
 * line's own split.
 */
function LineVersions({
  body,
  errors,
  setValues,
  hints,
  disabled,
}: CustomWidgetProps) {
  const context = contextOf(hints[VERSION_SPLIT]?.widget_context);
  const [typedEntered, setTypedEntered] = useState<(number | null)[] | null>(
    null,
  );
  const offered = quantitiesOffered(body);
  const several = offered.length > 1;
  const entered = body[VERSION_SPLIT] === "entered";
  const mustBeEven = several && entered;
  useEffect(() => {
    if (!mustBeEven) return;
    setTypedEntered(null);
    setValues({ [VERSION_SPLIT]: "even", [VERSION_QUANTITIES]: undefined });
  }, [mustBeEven, setValues]);
  if (!context) return null;

  if (several) {
    return (
      <section aria-label="Versions" className="space-y-1.5">
        <div className="text-sm font-medium">
          {context.count} versions share each quantity
        </div>
        <p className="text-sm text-muted-foreground">
          Each quantity is priced for {context.count} versions. The customer
          will give each version's quantity when they choose a quantity.
        </p>
      </section>
    );
  }

  const quantity = offered[0] ?? 0;
  const fromBody = Array.isArray(body[VERSION_QUANTITIES])
    ? (body[VERSION_QUANTITIES] as number[])
    : [];
  const value: VersionsValue = {
    count: context.count,
    split: entered ? "entered" : "even",
    entered: typedEntered ?? fromBody,
  };

  const change = (next: VersionsValue) => {
    setTypedEntered(next.split === "entered" ? next.entered : null);
    const request = versionsRequestFields(next);
    setValues(
      {
        [VERSION_SPLIT]: request.versionSplit,
        [VERSION_QUANTITIES]: request.versionQuantities,
      },
      { debounce: next.split === "entered" && value.split === "entered" },
    );
  };

  return (
    <section aria-label="Versions" className="space-y-1.5">
      <div className="text-sm font-medium">
        {context.count} versions share this quantity
      </div>
      <VersionsSplit
        value={value}
        quantity={quantity}
        quantityStep={context.step}
        errors={versionsFieldMessages(errors)}
        disabled={disabled}
        idPrefix="change-quantities-versions"
        labels={context.labels}
        onValueChange={change}
      />
    </section>
  );
}

export const lineVersionsWidget: CustomWidget = {
  ownedPaths: [VERSION_SPLIT, VERSION_QUANTITIES],
  Component: LineVersions,
  placement: "after_fields",
};
